import React from 'react';
import { createPortal } from 'react-dom';
import { DIALOG_SIZES } from './constants';

/**
 * BaseDialog Component
 * 
 * A reusable base dialog wrapper that provides consistent layout and behavior
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {string} title - Dialog title
 * @param {function} onClose - Callback when dialog closes
 * @param {React.ReactNode} children - Dialog content
 * @param {React.ReactNode} footer - Dialog footer content (buttons)
 * @param {string} size - Dialog size ('small', 'medium', 'large')
 * @param {boolean} showCloseButton - Whether to show close button in header
 */
const BaseDialog = ({
  isOpen,
  title,
  onClose,
  children,
  footer,
  size = 'medium',
  showCloseButton = true,
}) => {
  if (!isOpen) return null;

  const sizeClass = DIALOG_SIZES[size] || DIALOG_SIZES.medium;

  const dialogContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-xl shadow-2xl ${sizeClass} w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close dialog"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render dialog outside the normal DOM hierarchy to avoid overflow issues
  return createPortal(dialogContent, document.body);
};

export default BaseDialog;

